import api from './api';
import { Poolzeichnung, PoolzeichnungRequest } from '../types';

export type Ansicht = 'grundriss' | 'tiefe' | 'blatt';

/** Fehlermeldung aus einer Antwort, die als Text oder Blob statt JSON kam. */
const fehlerAusAntwort = async (err: unknown): Promise<Error> => {
  const daten = (err as { response?: { data?: unknown } })?.response?.data;
  try {
    const text = daten instanceof Blob ? await daten.text() : String(daten ?? '');
    const json = JSON.parse(text);
    if (json?.error) return new Error(json.error);
  } catch {
    // keine verwertbare Meldung
  }
  return err instanceof Error ? err : new Error('Unbekannter Fehler');
};

const speichereDatei = (blob: Blob, dateiname: string) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = dateiname;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

const dateinameAusHeader = (header: string | undefined, fallback: string) =>
  (header || '').match(/filename[^;=\n]*=([^;\n]*)/)?.[1]?.replace(/['"]/g, '').trim() || fallback;

/** SVG in ein PNG mit [faktor]-facher Auflösung umrechnen (für Messenger, E-Mail, Ausdruck). */
const svgZuPng = (svg: string, faktor = 2): Promise<Blob> =>
  new Promise((resolve, reject) => {
    const url = URL.createObjectURL(new Blob([svg], { type: 'image/svg+xml' }));
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.width * faktor;
      canvas.height = img.height * faktor;
      const ctx = canvas.getContext('2d');
      if (!ctx) { URL.revokeObjectURL(url); reject(new Error('Bild konnte nicht erzeugt werden')); return; }
      ctx.scale(faktor, faktor);
      ctx.drawImage(img, 0, 0);
      URL.revokeObjectURL(url);
      canvas.toBlob(b => (b ? resolve(b) : reject(new Error('Bild konnte nicht erzeugt werden'))), 'image/png');
    };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('Bild konnte nicht erzeugt werden')); };
    img.src = url;
  });

export const poolzeichnungService = {
  getByKundeId: async (kundeId: number): Promise<Poolzeichnung[]> =>
    (await api.get(`/poolzeichnungen/kunde/${kundeId}`)).data,

  getById: async (id: number): Promise<Poolzeichnung> => (await api.get(`/poolzeichnungen/${id}`)).data,

  create: async (data: PoolzeichnungRequest): Promise<Poolzeichnung> =>
    (await api.post('/poolzeichnungen', data)).data,

  update: async (id: number, data: PoolzeichnungRequest): Promise<Poolzeichnung> =>
    (await api.put(`/poolzeichnungen/${id}`, data)).data,

  delete: async (id: number): Promise<void> => {
    await api.delete(`/poolzeichnungen/${id}`);
  },

  /** SVG des (ungespeicherten) Formularstands; wirft bei ungültigen Maßen die Meldung des Backends. */
  vorschau: async (data: PoolzeichnungRequest, ansicht: Ansicht, signal?: AbortSignal): Promise<string> => {
    try {
      return (await api.post('/poolzeichnungen/vorschau', data, { params: { ansicht }, responseType: 'text', signal })).data;
    } catch (err) {
      throw await fehlerAusAntwort(err);
    }
  },

  downloadPdf: async (id: number): Promise<void> => {
    const response = await api.get(`/poolzeichnungen/${id}/pdf`, { responseType: 'blob' });
    speichereDatei(
      new Blob([response.data], { type: 'application/pdf' }),
      dateinameAusHeader(response.headers['content-disposition'], `Poolzeichnung_${id}.pdf`)
    );
  },

  /** PDF des Formularstands, ohne vorher zu speichern. */
  downloadPdfAusFormular: async (data: PoolzeichnungRequest): Promise<void> => {
    try {
      const response = await api.post('/poolzeichnungen/pdf', data, { responseType: 'blob' });
      speichereDatei(
        new Blob([response.data], { type: 'application/pdf' }),
        dateinameAusHeader(response.headers['content-disposition'], 'Poolzeichnung.pdf')
      );
    } catch (err) {
      throw await fehlerAusAntwort(err);
    }
  },

  /** Grundriss und Tiefenaufbau auf einem Bild als PNG. */
  downloadPng: async (data: PoolzeichnungRequest, dateiname: string): Promise<void> => {
    const svg = await poolzeichnungService.vorschau(data, 'blatt');
    speichereDatei(await svgZuPng(svg), dateiname);
  },
};

export default poolzeichnungService;
